import React, { useCallback, useMemo } from 'react';

interface ProgressBarProps {
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  className?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ currentTime, duration, onSeek, className = '' }) => {
  const progressPercentage = useMemo(() => {
    if (duration <= 0) {
      return 0;
    }
    const percentage = (currentTime / duration) * 100;
    return Math.max(0, Math.min(100, percentage));
  }, [currentTime, duration]);

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, clickX / rect.width));
      const newTime = percentage * duration;

      onSeek(newTime);
    },
    [duration, onSeek]
  );

  return (
    <div
      className={`pointer-events-auto relative flex h-6 w-full cursor-pointer items-center ${className}`}
      role="slider"
      tabIndex={0}
      aria-valuemin={0}
      aria-valuemax={duration}
      aria-valuenow={currentTime}
      aria-label="Video progress"
      onClick={handleProgressClick}
      data-testid="progress-bar"
    >
      <div className="relative h-1.5 w-full cursor-pointer transition-all ease-in-out">
        <div className="relative size-full overflow-hidden rounded-[1.5px] bg-white/30">
          <div
            className="absolute left-0 top-0 h-full bg-white"
            style={{ width: `${progressPercentage}%` }}
            data-testid="progress-fill"
          />
        </div>
      </div>
      <div
        className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white transition-opacity"
        style={{ left: `${progressPercentage}%` }}
        data-testid="progress-knob"
      />
    </div>
  );
};

export default ProgressBar;
